import { useRef, useState, useEffect } from 'react';
import { DateTimePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { PickerValue } from '@mui/x-date-pickers/internals';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';

interface DateTimeInputProps {
  value: PickerValue | string | Date;
  type?: 'date' | 'datetime' | 'time';
  onChange: (date: PickerValue) => void;
  sx?: any;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
  [key: string]: any;
}

function DateTimeInput({
  value,
  type = 'datetime',
  sx,
  onChange,
  size = 'small',
  error,
  helperText,
  fullWidth = true,
  ...props
}: DateTimeInputProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<PickerValue>(value ? dayjs(value) : null);
  const selected = useRef(false);

  // Sync internalValue when parent's value prop changes, but not while picker is open
  useEffect(() => {
    if (!open) setInternalValue(value ? dayjs(value) : null);
  }, [value, open]);

  const isDark = theme.palette.mode === 'dark';

  const inputSx = {
    height: '44px',
    borderRadius: 2,
    backgroundColor: theme.palette.background.inputGrey,
    '& .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'transparent' : theme.palette.border.light,
    },
    '&:hover .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.medium,
    },
    '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.purple,
    },
  };

  // Only set onFocus for non-time pickers to avoid cross-triggering
  const textFieldSlotProps = {
    fullWidth,
    size,
    variant: 'outlined' as const,
    error,
    helperText,
    ...(type !== 'time' && {
      onFocus: () => {
        if (!selected.current) setOpen(true);
      },
      onBlur: () => {
        selected.current = false;
      },
    }),
    sx: {
      width: fullWidth ? '100%' : undefined,
      ...sx,
    },
    InputProps: { sx: inputSx },
  };

  if (type === 'time') {
    // TimePicker uses MultiSectionDigitalClock which fires onChange ONLY on click
    // (not on mouse-wheel scroll), so updating state in onChange is safe — no CPU loop.
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          open={open}
          value={internalValue}
          ampm={false}
          onChange={(newValue) => {
            setInternalValue(newValue);
          }}
          onAccept={(newValue) => {
            onChange(newValue ?? internalValue);
            setInternalValue(newValue ?? internalValue);
            selected.current = true;
            setOpen(false);
          }}
          onClose={() => {
            setOpen(false);
            selected.current = true;
          }}
          slotProps={{
            openPickerButton: {
              onClick: () => { setOpen(true); selected.current = true; },
            },
            textField: textFieldSlotProps,
          }}
          {...props}
        />
      </LocalizationProvider>
    );
  }

  const shortcuts = [
    { label: 'Today', getValue: () => dayjs() },
    { label: '3 Days Ago', getValue: () => dayjs().subtract(3, 'day') },
    { label: '7 Days Ago', getValue: () => dayjs().subtract(7, 'day') },
  ];

  const views =
    type === 'date'
      ? (['year', 'month', 'day'] as const)
      : (['year', 'month', 'day', 'hours', 'minutes'] as const);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        open={open}
        value={internalValue}
        views={views as any}
        ampm={false}
        onChange={(newValue) => {
          setInternalValue(newValue);
        }}
        onAccept={(newValue) => {
          onChange(newValue ?? internalValue);
          setInternalValue(newValue ?? internalValue);
          selected.current = true;
          setOpen(false);
        }}
        onClose={() => {
          setOpen(false);
          selected.current = true;
        }}
        slotProps={{
          shortcuts: { items: shortcuts },
          day: (ownerState) => ({
            onDoubleClick: () => {
              if (ownerState.day) {
                onChange(ownerState.day);
                setInternalValue(ownerState.day);
                selected.current = true;
                setOpen(false);
              }
            },
          }),
          openPickerButton: {
            onClick: () => { setOpen(true); selected.current = true; },
          },
          textField: textFieldSlotProps,
        }}
        {...props}
      />
    </LocalizationProvider>
  );
}

export default DateTimeInput;
